import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface TurnstileCaptchaProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpired?: () => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
}

export interface TurnstileCaptchaHandle {
  reset: () => void;
}

export const TurnstileCaptcha = forwardRef<TurnstileCaptchaHandle, TurnstileCaptchaProps>(({
  siteKey,
  onSuccess,
  onError,
  onExpired,
  theme = 'light',
  size = 'normal',
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasRenderedRef = useRef(false);

  const resetWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setError(null);
      onSuccessRef.current?.('');
    }
  }, []);

  useImperativeHandle(ref, () => ({ reset: resetWidget }), [resetWidget]);

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    const loadTurnstile = () => {
      if (!window.turnstile) {
        const existingScript = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
        if (existingScript) {
          const checkLoaded = () => {
            if (window.turnstile) {
              setIsLoaded(true);
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => setIsLoaded(true);
        script.onerror = () => {
          setError('Failed to load captcha');
          onErrorRef.current?.();
        };
        document.head.appendChild(script);
      } else {
        setIsLoaded(true);
      }
    };

    loadTurnstile();
  }, [siteKey]);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !siteKey || hasRenderedRef.current) {
      return;
    }

    const turnstile = window.turnstile;
    if (!turnstile) {
      return;
    }

    const widgetId = turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme,
      size,
      callback: (token: string) => {
        onSuccessRef.current(token);
      },
      'expired-callback': () => {
        setError('Captcha expired. Please verify again.');
        onSuccessRef.current?.('');
        onExpiredRef.current?.();
      },
      'error-callback': () => {
        setError('Captcha error. Please try again.');
        onErrorRef.current?.();
      },
    });
    widgetIdRef.current = widgetId;
    hasRenderedRef.current = true;

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        hasRenderedRef.current = false;
      }
    };
  }, [isLoaded, siteKey, theme, size]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className="captcha-container w-full max-w-[300px]">
      <div ref={containerRef} />
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
});

TurnstileCaptcha.displayName = 'TurnstileCaptcha';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark';
          size?: 'normal' | 'compact';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

export default TurnstileCaptcha;